import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CircleAlert, ExternalLink, Loader2, RefreshCw } from "lucide-react";

import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { BatchDetailLink, SalesOrderDetailLink, ShipmentDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { formatShipToBlock } from "@/lib/formatShipToBlock";
import { buildShippingOutboundPathForShipment } from "@/lib/hubEntityDetailPaths";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { cn } from "@/lib/utils";
import {
  fetchShipmentBrowseList,
  fetchShipmentDetail,
  fetchShipmentSerials,
  fetchShipmentSerialsPortal,
  filterShipmentBrowseRowsClient,
  type ShipmentBrowseListRow,
  type ShipmentDetail,
  type ShipmentSerialRow,
} from "@/pages/shipping/shipmentDetailsApi";

const SHIPMENT_DETAILS_HELP =
  "Read-only shipment context with batch details, loaded SKUs, sales orders, carrier and tracking, and device serials. Use Outbound for mark-ready and ship actions.";

const PORTAL_COLUMNS_BLUR_DELAY_MS = 1000;
const WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE =
  "The Wireless_Prod database is inaccessible in local development. Portal org and group load on the production hub only.";

function formatShortDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    const [, yyyy, mm, dd] = match;
    return `${mm}-${dd}-${yyyy}`;
  }
  return value;
}

function wirelessPortalDeviceDetailsUrl(imei: string): string {
  return `https://portal-sgs.com/device-details/?id=${encodeURIComponent(imei.trim())}`;
}

function shipmentSerialPortalFieldCell(
  value: string | null | undefined,
  options: {
    rowHasImei: boolean;
    pending: boolean;
    failed: boolean;
    errorMessage: string;
    fieldLabel: string;
  },
): ReactNode {
  if (options.pending) {
    return "Loading…";
  }
  if (options.failed && options.rowHasImei) {
    return (
      <HoverHintTrigger
        trigger={
          <CircleAlert
            className="h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label={`${options.fieldLabel} could not be loaded from wireless portal`}
          />
        }
      >
        {options.errorMessage}
      </HoverHintTrigger>
    );
  }
  return value != null && value.trim() !== "" ? value : "—";
}

type ShipmentPipelineStatusInput = Pick<
  ShipmentBrowseListRow,
  "sent_to_pack_out" | "ready_for_pickup" | "date_shipped"
>;

type ShipmentPipelineBadgeVariant = "browse" | "detail";

function shipmentPipelineStatusBadge(
  input: ShipmentPipelineStatusInput,
  variant: ShipmentPipelineBadgeVariant,
): ReactNode {
  const className =
    variant === "browse"
      ? "whitespace-nowrap px-1.5 text-[10px]"
      : "text-[10px]";

  if (input.date_shipped != null) {
    const label = variant === "browse" ? "Shipped" : `Shipped ${formatShortDate(input.date_shipped)}`;
    return (
      <Badge key="shipped" tone="muted" className={className}>
        {label}
      </Badge>
    );
  }
  if (input.ready_for_pickup) {
    const label = variant === "browse" ? "Ready" : "Ready for pickup";
    return (
      <Badge key="ready" tone="success" className={className}>
        {label}
      </Badge>
    );
  }
  if (input.sent_to_pack_out) {
    const label = variant === "browse" ? "Pack out" : "Sent to pack out";
    return (
      <Badge key="pack-out" tone="neutral" className={className}>
        {label}
      </Badge>
    );
  }
  return null;
}

function shipmentPipelineStatusLabel(input: ShipmentPipelineStatusInput): string | null {
  if (input.date_shipped != null) {
    return `Shipped ${formatShortDate(input.date_shipped)}`;
  }
  if (input.ready_for_pickup) {
    return "Ready for pickup";
  }
  if (input.sent_to_pack_out) {
    return "Pack out";
  }
  return null;
}

function shipmentStatusBadges(shipment: ShipmentDetail): ReactNode {
  const badges: ReactNode[] = [];
  if (shipment.batch_shipment_on_hold) {
    badges.push(
      <Badge key="hold" tone="warning" className="text-[10px]">
        Batch shipment hold
      </Badge>,
    );
  }
  const pipelineBadge = shipmentPipelineStatusBadge(shipment, "detail");
  if (pipelineBadge != null) {
    badges.push(pipelineBadge);
  }
  return badges.length > 0 ? <div className="flex flex-wrap gap-1.5">{badges}</div> : null;
}

function shipmentBrowseStatusBadges(row: ShipmentBrowseListRow): ReactNode {
  const badge = shipmentPipelineStatusBadge(row, "browse");
  return badge != null ? <div className="mt-0.5 flex flex-wrap gap-1">{badge}</div> : null;
}

function ShipmentLoadedLinesTable({ lines }: { lines: ShipmentDetail["lines"] }) {
  if (lines.length === 0) {
    return <p className="text-sm text-slate-600">No shipment summary lines yet.</p>;
  }

  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[760px] text-left text-xs text-slate-800">
        <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5">SKU</th>
            <th className="px-2 py-1.5">Build order</th>
            <th className="px-2 py-1.5 text-right tabular-nums">Qty</th>
            <th className="px-2 py-1.5 text-right tabular-nums">Assembled</th>
            <th className="px-2 py-1.5">Sales order</th>
            <th className="px-2 py-1.5">Customer PO</th>
            <th className="px-2 py-1.5">SGS ship by</th>
            <th className="px-2 py-1.5 tabular-nums">Batch item</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.batch_item_id} className="border-b border-slate-50 last:border-none">
              <td className="min-w-0 px-2 py-1.5 align-top font-mono text-[11px]">
                {line.sku.trim() !== "" ? (
                  <MrpSkuDetailLink
                    sku={line.sku}
                    className="block break-all font-medium text-brand-700 no-underline hover:text-brand-900"
                  />
                ) : (
                  "—"
                )}
              </td>
              <td className="px-2 py-1.5 align-top">{line.build_order_number ?? "—"}</td>
              <td className="px-2 py-1.5 text-right align-top tabular-nums">{line.quantity}</td>
              <td className="px-2 py-1.5 text-right align-top tabular-nums">{line.quantity_assembled}</td>
              <td className="px-2 py-1.5 align-top">
                {line.sales_order_id != null ? (
                  <SalesOrderDetailLink
                    salesOrderId={line.sales_order_id}
                    salesOrderNumber={line.sales_order_number}
                    className="text-[11px]"
                  />
                ) : (
                  "—"
                )}
              </td>
              <td className="px-2 py-1.5 align-top">{line.customer_purchase_order_number ?? "—"}</td>
              <td className="px-2 py-1.5 align-top">{formatShortDate(line.sgs_ship_by_date)}</td>
              <td className="px-2 py-1.5 align-top tabular-nums">{line.batch_item_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShipmentSerialsPanel({ shipmentId, serialCount }: { shipmentId: number; serialCount: number }) {
  const [serialsOpen, setSerialsOpen] = useState(false);

  const serialsQuery = useQuery<ShipmentSerialRow[], ApiError>({
    queryKey: ["shipping", "shipment-detail", "serials", shipmentId],
    queryFn: () => fetchShipmentSerials(shipmentId),
    enabled: serialsOpen && serialCount > 0,
  });

  const serialsHasImei = useMemo(
    () => (serialsQuery.data ?? []).some((row) => (row.imei ?? "").trim() !== ""),
    [serialsQuery.data],
  );
  const portalLookupDevBlocked = import.meta.env.DEV && serialsHasImei;

  const serialsPortalQuery = useQuery<
    Record<
      string,
      {
        wireless_portal_org_name: string | null;
        wireless_portal_group_names: string | null;
      }
    >,
    ApiError
  >({
    queryKey: ["shipping", "shipment-detail", "serials-portal", shipmentId],
    queryFn: () => fetchShipmentSerialsPortal(shipmentId),
    enabled: serialsOpen && serialCount > 0 && serialsHasImei && !import.meta.env.DEV,
  });

  const [portalColumnsBlurred, setPortalColumnsBlurred] = useState(false);
  useEffect(() => {
    if (portalLookupDevBlocked || !serialsHasImei || !serialsPortalQuery.isFetching) {
      setPortalColumnsBlurred(false);
      return;
    }
    setPortalColumnsBlurred(true);
    const timer = window.setTimeout(() => setPortalColumnsBlurred(false), PORTAL_COLUMNS_BLUR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [portalLookupDevBlocked, serialsHasImei, serialsPortalQuery.isFetching, serialsPortalQuery.fetchStatus]);

  const displaySerialRows = useMemo(() => {
    const baseRows = serialsQuery.data ?? [];
    const portalByImei = serialsPortalQuery.data ?? {};
    return baseRows.map((row) => {
      const imei = (row.imei ?? "").trim();
      const portal = imei !== "" ? portalByImei[imei] : undefined;
      return {
        ...row,
        wireless_portal_org_name: portal?.wireless_portal_org_name ?? null,
        wireless_portal_group_names: portal?.wireless_portal_group_names ?? null,
      };
    });
  }, [serialsQuery.data, serialsPortalQuery.data]);

  const portalColumnsPending =
    serialsHasImei && portalColumnsBlurred && serialsPortalQuery.isFetching;
  const portalColumnsFailed =
    serialsHasImei && (portalLookupDevBlocked || serialsPortalQuery.isError);
  const portalColumnsErrorMessage = portalLookupDevBlocked
    ? WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE
    : serialsPortalQuery.error?.message ?? "Wireless portal org and group could not be loaded.";

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 py-3">
        <CardTitleWithHelp hoverHint="Device label, IMEI, wireless portal org and group, and box label serials assigned to this shipment. Device label opens hub device lookup; the external link opens the wireless portal device page when an IMEI is present.">
          Serial numbers
        </CardTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          {serialsHasImei && !import.meta.env.DEV ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={serialsPortalQuery.isFetching || !serialsOpen}
              onClick={() => {
                void serialsPortalQuery.refetch();
              }}
            >
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", serialsPortalQuery.isFetching && "animate-spin")}
                aria-hidden
              />
              Refresh portal
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-expanded={serialsOpen}
            onClick={() => setSerialsOpen((open) => !open)}
          >
            {serialsOpen ? (
              <ChevronDown className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            {serialsOpen ? "Hide serials" : `Show serials (${String(serialCount)})`}
          </Button>
        </div>
      </CardHeader>
      {serialsOpen ? (
        <CardBody className="p-0">
          {serialCount === 0 ? (
            <p className="px-4 py-3 text-sm text-slate-600">No serials assigned to this shipment yet.</p>
          ) : null}
          {serialCount > 0 && serialsQuery.isPending && serialsQuery.data == null ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading serials…
            </div>
          ) : null}
          {serialsQuery.isError ? (
            <p className="mx-4 my-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {serialsQuery.error.message}
            </p>
          ) : null}
          {serialsQuery.data != null ? (
            <div className="-mx-1">
              {displaySerialRows.length === 0 ? (
                <p className="px-4 py-3 text-sm text-slate-600">No serials assigned to this shipment yet.</p>
              ) : (
                <div className="max-h-[min(28rem,50vh)] overflow-auto">
                  <table className="w-full min-w-[640px] border-collapse text-left text-xs text-slate-800">
                    <thead className="sticky top-0 z-[1] border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                      <tr>
                        <th className="px-2 py-1.5">Device label</th>
                        <th className="px-2 py-1.5">IMEI</th>
                        <th className="px-2 py-1.5">Portal org</th>
                        <th className="px-2 py-1.5">Portal group</th>
                        <th className="px-2 py-1.5">Finished SKU</th>
                        <th className="px-2 py-1.5">Box label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displaySerialRows.map((row) => {
                        const rowHasImei = (row.imei ?? "").trim() !== "";
                        return (
                          <tr key={row.serial_fci_id} className="border-b border-slate-50 last:border-none">
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">
                              {row.device_label_serial != null && row.device_label_serial.trim() !== "" ? (
                                <span className="inline-flex items-center gap-1">
                                  <Link
                                    className="font-medium text-brand-700 hover:text-brand-900"
                                    to={`/manufacturing/device-lookup?serial=${encodeURIComponent(row.device_label_serial)}`}
                                  >
                                    {row.device_label_serial}
                                  </Link>
                                  {row.imei != null && row.imei.trim() !== "" ? (
                                    <a
                                      href={wirelessPortalDeviceDetailsUrl(row.imei)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex shrink-0 rounded text-slate-400 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                                      aria-label={`Open wireless portal for ${row.device_label_serial}`}
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                                    </a>
                                  ) : null}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.imei ?? "—"}</td>
                            <td
                              className={cn(
                                "px-2 py-1.5 align-top transition-[filter] duration-200",
                                portalColumnsPending && "blur-sm select-none text-slate-400",
                              )}
                            >
                              {shipmentSerialPortalFieldCell(row.wireless_portal_org_name, {
                                rowHasImei,
                                pending: portalColumnsPending,
                                failed: portalColumnsFailed,
                                errorMessage: portalColumnsErrorMessage,
                                fieldLabel: "Portal org",
                              })}
                            </td>
                            <td
                              className={cn(
                                "px-2 py-1.5 align-top transition-[filter] duration-200",
                                portalColumnsPending && "blur-sm select-none text-slate-400",
                              )}
                            >
                              {shipmentSerialPortalFieldCell(row.wireless_portal_group_names, {
                                rowHasImei,
                                pending: portalColumnsPending,
                                failed: portalColumnsFailed,
                                errorMessage: portalColumnsErrorMessage,
                                fieldLabel: "Portal group",
                              })}
                            </td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.finished_sku ?? "—"}</td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.box_serial ?? "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </CardBody>
      ) : null}
    </Card>
  );
}

export function ShipmentDetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const shipmentIdParam = searchParams.get("shipment_id") ?? "";
  const searchParam = searchParams.get("search") ?? "";
  const [searchText, setSearchText] = useState(() => searchParam);

  const selectedShipmentId = useMemo(() => {
    const parsed = Number.parseInt(shipmentIdParam.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [shipmentIdParam]);

  useEffect(() => {
    setSearchText(searchParam);
  }, [searchParam]);

  const listQuery = useQuery<ShipmentBrowseListRow[], ApiError>({
    queryKey: ["shipping", "shipment-details", "list", searchParam.trim()],
    queryFn: () => fetchShipmentBrowseList(searchParam.trim(), 100),
    staleTime: 30_000,
  });

  const detailQuery = useQuery<ShipmentDetail, ApiError>({
    queryKey: ["shipping", "shipment-detail", selectedShipmentId],
    queryFn: () => fetchShipmentDetail(selectedShipmentId!),
    enabled: selectedShipmentId != null,
  });

  const filteredRows = useMemo(
    () => filterShipmentBrowseRowsClient(listQuery.data ?? [], searchText),
    [listQuery.data, searchText],
  );

  const searchActive = searchText.trim() !== "";

  const openShipment = useCallback(
    (shipmentId: number) => {
      const next = new URLSearchParams(searchParams);
      next.set("shipment_id", String(shipmentId));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  function updateSearchFilter(value: string): void {
    setSearchText(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed === "") {
      next.delete("search");
    } else {
      next.set("search", value);
    }
    setSearchParams(next, { replace: true });
  }

  function handleShipmentRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, shipmentId: number): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openShipment(shipmentId);
    }
  }

  const shipment = detailQuery.data;

  const batchShipToBlock = useMemo(() => {
    if (shipment == null) return null;
    return formatShipToBlock({
      ship_to_recipient: shipment.batch_ship_to_recipient,
      ship_to_address_line_1: shipment.batch_ship_to_address_line_1,
      ship_to_address_line_2: shipment.batch_ship_to_address_line_2,
      ship_to_address_line_3: shipment.batch_ship_to_address_line_3,
      ship_to_city: shipment.batch_ship_to_city,
      ship_to_state: shipment.batch_ship_to_state,
      ship_to_zip: shipment.batch_ship_to_zip,
      ship_to_country: shipment.batch_ship_to_country,
    });
  }, [shipment]);

  const listCountLabel = (
    <span className="font-normal text-slate-600">
      ({filteredRows.length}
      {searchActive && listQuery.data != null ? ` of ${String(listQuery.data.length)}` : ""})
    </span>
  );

  const trackingNumber = shipment?.tracking != null ? String(shipment.tracking).trim() : "";
  const tabBreadcrumbLabel =
    trackingNumber.length > 0
      ? trackingNumber
      : shipment != null
        ? `Shipment ${String(shipment.shipment_id)}`
        : selectedShipmentId != null
          ? `Shipment ${String(selectedShipmentId)}`
          : "Shipment details";

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <PageTitleWithHelp hoverHint={SHIPMENT_DETAILS_HELP} breadcrumbLabel={tabBreadcrumbLabel}>
          Shipment details
        </PageTitleWithHelp>
        {selectedShipmentId != null ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to={buildShippingOutboundPathForShipment(selectedShipmentId)}
              className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Open in Outbound
            </Link>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void detailQuery.refetch()}
              disabled={detailQuery.isFetching}
            >
              {detailQuery.isFetching ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              )}
              Refresh
            </Button>
          </div>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="shipment-details-search">
            Search shipments
          </label>
          <Input
            id="shipment-details-search"
            type="search"
            className="h-10"
            placeholder="Shipment id, batch id, SKU, GSO, tracking, carrier…"
            value={searchText}
            onChange={(event) => updateSearchFilter(event.target.value)}
          />
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        <HubViewportCard>
          <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>Shipments {listCountLabel}</CardTitle>
            {listQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden /> : null}
          </CardHeader>
          <CardBody className="flex min-h-0 flex-1 flex-col p-0">
            {listQuery.isError ? (
              <p className="mx-5 my-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {listQuery.error.message}
              </p>
            ) : null}
            {listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">Loading shipments…</p>
            ) : null}
            {listQuery.data != null && filteredRows.length === 0 && !listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">No shipments match these filters.</p>
            ) : null}
            {filteredRows.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <table className="w-full table-fixed border-collapse text-left text-xs text-slate-800">
                  <colgroup>
                    <col className="w-[8.5rem]" />
                    <col className="w-[4rem]" />
                    <col />
                    <col className="w-[5.5rem]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr>
                      <th className="px-3 py-2">Shipment</th>
                      <th className="px-2 py-2">Batch</th>
                      <th className="px-2 py-2">SKUs</th>
                      <th className="px-3 py-2">Shipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const isSelected = selectedShipmentId === row.shipment_id;
                      return (
                        <tr
                          key={row.shipment_id}
                          role="button"
                          tabIndex={0}
                          aria-selected={isSelected}
                          onClick={() => openShipment(row.shipment_id)}
                          onKeyDown={(event) => handleShipmentRowKeyDown(event, row.shipment_id)}
                          className={cn(
                            "cursor-pointer border-b border-slate-50 last:border-none hover:bg-slate-50/90",
                            isSelected && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                        >
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="font-mono tabular-nums font-medium text-slate-900">{row.shipment_id}</span>
                              {row.batch_location_id != null && row.batch_location_id > 0 ? (
                                <ManufacturingLocationChip
                                  locationId={row.batch_location_id}
                                  fallbackLabel={row.batch_location_label}
                                />
                              ) : row.batch_location_label.trim() !== "" ? (
                                <span className="text-[11px] text-slate-600">{row.batch_location_label}</span>
                              ) : null}
                            </div>
                            {shipmentBrowseStatusBadges(row)}
                          </td>
                          <td className="px-2 py-2 align-top tabular-nums">{row.batch_id ?? "—"}</td>
                          <td className="px-2 py-2 align-top">
                            <div className="truncate font-mono text-[11px] text-slate-700" title={row.sku_summary ?? undefined}>
                              {row.sku_summary ?? "—"}
                            </div>
                            {row.sales_order_summary ? (
                              <div className="truncate text-[11px] text-slate-500" title={row.sales_order_summary}>
                                {row.sales_order_summary}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap tabular-nums">
                            {formatShortDate(row.date_shipped ?? row.date_created)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardBody>
        </HubViewportCard>

        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          {selectedShipmentId == null ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-600">
                  Select a shipment from the list or follow a shipment link from batch details, paperwork, or device lookup.
                </p>
              </CardBody>
            </Card>
          ) : detailQuery.isLoading ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-500">Loading shipment…</p>
              </CardBody>
            </Card>
          ) : detailQuery.isError ? (
            <Card>
              <CardBody>
                <p className="text-sm text-red-700">{detailQuery.error.message}</p>
              </CardBody>
            </Card>
          ) : shipment != null ? (
            <>
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
                  <div className="space-y-2">
                    <CardTitle>
                      Shipment{" "}
                      <ShipmentDetailLink shipmentId={shipment.shipment_id} className="tabular-nums">
                        {shipment.shipment_id}
                      </ShipmentDetailLink>
                    </CardTitle>
                    {shipmentStatusBadges(shipment)}
                  </div>
                </CardHeader>
                <CardBody className="grid gap-4 lg:grid-cols-3">
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Batch</dt>
                      <dd className="mt-0.5">
                        <BatchDetailLink batchId={shipment.batch_id} />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Site</dt>
                      <dd className="mt-0.5">
                        {shipment.batch_location_id != null ? (
                          <ManufacturingLocationChip
                            locationId={shipment.batch_location_id}
                            fallbackLabel={shipment.batch_location_label}
                          />
                        ) : (
                          shipment.batch_location_label
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ship to</dt>
                      <dd className="mt-0.5 whitespace-pre-wrap">{batchShipToBlock ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Manufacturer SO</dt>
                      <dd className="mt-0.5">{shipment.batch_manufacturer_sales_order_number ?? "—"}</dd>
                    </div>
                  </dl>
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</dt>
                      <dd className="mt-0.5">{formatShortDate(shipment.date_created)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Batch ship by</dt>
                      <dd className="mt-0.5">{formatShortDate(shipment.batch_mfr_ship_by_date)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sent to pack out</dt>
                      <dd className="mt-0.5">
                        {shipment.sent_to_pack_out ? formatShortDate(shipment.date_sent_to_pack_out) : "No"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Serials on shipment</dt>
                      <dd className="mt-0.5 tabular-nums">{shipment.serial_count.toLocaleString()}</dd>
                    </div>
                  </dl>
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Carrier</dt>
                      <dd className="mt-0.5">{shipment.carrier ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tracking</dt>
                      <dd className="mt-0.5 font-mono text-xs">{shipment.tracking ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date shipped</dt>
                      <dd className="mt-0.5">{formatShortDate(shipment.date_shipped)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Note</dt>
                      <dd className="mt-0.5 whitespace-pre-wrap">{shipment.note ?? "—"}</dd>
                    </div>
                  </dl>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitleWithHelp hoverHint="Sales orders with batch lines loaded on this shipment.">
                    Sales orders
                  </CardTitleWithHelp>
                </CardHeader>
                <CardBody className="p-0">
                  {shipment.sales_orders.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-slate-600">No sales order lines are loaded on this shipment.</p>
                  ) : (
                    <div className="-mx-1 overflow-x-auto">
                      <table className="w-full min-w-[640px] text-left text-xs text-slate-800">
                        <thead className="border-b border-slate-200 bg-slate-50/90 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="px-2 py-1.5">Sales order</th>
                            <th className="px-2 py-1.5">Client</th>
                            <th className="px-2 py-1.5">Customer PO</th>
                            <th className="px-2 py-1.5">SGS ship by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shipment.sales_orders.map((row) => (
                            <tr key={row.sales_order_id} className="border-b border-slate-50 last:border-none">
                              <td className="px-2 py-1.5 align-top">
                                <SalesOrderDetailLink
                                  salesOrderId={row.sales_order_id}
                                  salesOrderNumber={row.sales_order_number}
                                  className="text-[11px]"
                                />
                              </td>
                              <td className="px-2 py-1.5 align-top">{row.client_name ?? "—"}</td>
                              <td className="px-2 py-1.5 align-top">{row.customer_purchase_order_number ?? "—"}</td>
                              <td className="px-2 py-1.5 align-top">{formatShortDate(row.sgs_ship_by_date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitleWithHelp hoverHint="Batch lines loaded on this shipment with SKU, build order, and sales order context.">
                    Loaded lines
                  </CardTitleWithHelp>
                </CardHeader>
                <CardBody>
                  <ShipmentLoadedLinesTable lines={shipment.lines} />
                </CardBody>
              </Card>

              <ShipmentSerialsPanel key={selectedShipmentId} shipmentId={selectedShipmentId} serialCount={shipment.serial_count} />

              {shipment.batch_shipments.length > 1 ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Other shipments on batch {String(shipment.batch_id)}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ul className="space-y-2 text-sm">
                      {shipment.batch_shipments
                        .filter((row) => row.shipment_id !== shipment.shipment_id)
                        .map((row) => {
                          const statusLabel = shipmentPipelineStatusLabel(row);
                          return (
                            <li key={row.shipment_id} className="flex flex-wrap items-center gap-2">
                              <ShipmentDetailLink shipmentId={row.shipment_id} />
                              {statusLabel != null ? (
                                <span className="text-xs text-slate-500">{statusLabel}</span>
                              ) : null}
                            </li>
                          );
                        })}
                    </ul>
                  </CardBody>
                </Card>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
